import { useMemo } from 'react'
import { Page, useColourData } from '@sh24/ui-components'
import {
  AnchorModule,
  ButtonCloudModule,
  ConsultationAppointmentBookingModule,
  ContentModule,
  DynamicContentModule,
  GalleryModule,
  GridModule,
  HelpSnippetsModule,
  HighlightsModule,
  LocationModule,
  OrderStatusCheckerModule,
  OrderSummaryModule,
  SearchInputModule,
  SupportLinksModule,
  AvailabilityCheckerModule,
  ContraceptionComparisonToolModule,
  AvailabilityCheckerResultsModule,
  LoginModule,
  VerifyPhoneNumberModule,
  ResetPasswordEmailModule,
  ResetPasswordVerifyEmailModule,
  SignUpEmailModule,
  SignUpPasswordModule,
  SignUpVerifyEmailModule,
  SignUpPhoneNumberModule,
  SignUpVerifyPhoneNumberModule,
  MfaModule,
  MfaAddAuthenticatorModule,
} from '../../components/Modules'
import SectionContext from '../../contexts/section-context'
import SectionModule from '../../components/Modules/SectionModule/section-module'
import { getBackgroundColour, getDecoration } from '../config/helpers'

const moduleMap = {
  // contentful modules:
  buttonCloud: ButtonCloudModule,
  consultationAppointmentBooking: ConsultationAppointmentBookingModule,
  content: ContentModule,
  dynamicContent: DynamicContentModule,
  gallery: GalleryModule,
  grid: GridModule,
  helpSnippets: HelpSnippetsModule,
  highlights: HighlightsModule,
  location: LocationModule,
  orderStatusChecker: OrderStatusCheckerModule,
  orderSummary: OrderSummaryModule,
  searchInput: SearchInputModule,
  supportLinks: SupportLinksModule,
  availabilityChecker: AvailabilityCheckerModule,
  contraceptionComparisonTool: ContraceptionComparisonToolModule,
  availabilityCheckerResults: AvailabilityCheckerResultsModule,
  section: SectionModule,
  // programmatic modules:
  login: LoginModule,
  verifyPhoneNumber: VerifyPhoneNumberModule,
  resetPasswordEmail: ResetPasswordEmailModule,
  resetPasswordVerifyEmail: ResetPasswordVerifyEmailModule,
  signUpEmail: SignUpEmailModule,
  signUpPassword: SignUpPasswordModule,
  signUpVerifyEmail: SignUpVerifyEmailModule,
  signUpPhoneNumber: SignUpPhoneNumberModule,
  signUpVerifyPhoneNumber: SignUpVerifyPhoneNumberModule,
  mfa: MfaModule,
  mfaAddAuthenticator: MfaAddAuthenticatorModule,
}

/**
 * 29/05/2025
 * This component needs a serious rethink and no further extensions
 * should be made to it until we can sort it out.
 * Issues:
 * - renderModules causes template issues. this code can probably be
 *   removed as we are now able to put the offending callout this was
 *   created for inside a section.
 * - Likewise handling anchors means tracking a separate sectionIndex
 *   so as not to break the template colours. this needs to be simplified.
 *   Placing all anchors inside sections would solve this issue but there
 *   are quite a few of them in use. A better way to deal with anchors
 *   without huge contentful changes would be ideal.
 * - Invalid use of hooks inside map. Have had to disable the rules for now.
 */

const Sections = ({ config, modules, renderModules }) => {
  let sectionIndex = 0

  return modules?.map((module, index) => {
    const Component = moduleMap[module.contentType] || null
    if (!Component) return null

    const last = index === modules.length - 1

    const backgroundColour = getBackgroundColour({
      module,
      config,
      index: sectionIndex,
      last,
    })

    const nextBackgroundColour = getBackgroundColour({
      module: modules[index + 1],
      config,
      index: sectionIndex + 1,
      last: index + 1 === modules.length - 1,
    })

    const decoration = getDecoration({
      decorations: config.decorations,
      backgroundColour,
      nextBackgroundColour,
      index: sectionIndex,
      last,
    })

    const options = config.modules?.[module.contentType] || {}

    const prevModule = modules[index - 1]
    sectionIndex += 1

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const colourData = useColourData(backgroundColour)

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const sectionContextProviderValue = useMemo(
      () => ({
        backgroundColour,
        decoration,
        isDark: colourData.isDark,
      }),
      [backgroundColour, decoration, colourData],
    )

    return (
      <SectionContext.Provider key={module.id} value={sectionContextProviderValue}>
        <Page.Section
          backgroundColour={backgroundColour}
          decoration={decoration}
          hidden={!renderModules[module.id]}
          isDark={colourData.isDark}
        >
          <Page.Content>
            {prevModule?.contentType === 'anchor' ? <AnchorModule {...prevModule} /> : null}
            <Component {...module} {...options} />
          </Page.Content>
        </Page.Section>
      </SectionContext.Provider>
    )
  })
}

export default Sections
